import React, { useState } from 'react';
import { MapPin, Calendar, Image as ImageIcon } from 'lucide-react';
import PhotoLightbox from './PhotoLightbox';
import { slugify } from '../Public/Explore/services';

interface Photo {
  url: string;
  caption?: string;
  category?: string;
  uploadedAt: string;
}

interface Restaurant {
  restaurantId: string;
  restaurantName: string;
  restaurantCover?: string;
  photos: Photo[];
}

interface RestaurantCardProps {
  restaurant: Restaurant;
}

const RestaurantCard: React.FC<RestaurantCardProps> = ({ restaurant }) => {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
  const { restaurantId, restaurantName, restaurantCover, photos } = restaurant;

  const handlePhotoClick = (index: number) => {
    setSelectedPhotoIndex(index);
  };

  const handleCloseLightbox = () => {
    setSelectedPhotoIndex(null);
  };

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:border-gray-300 transition-colors">
        {/* Restaurant Header */}
        <div className="relative h-48 bg-gray-200 overflow-hidden">
          {restaurantCover ? (
            <img
              src={restaurantCover}
              alt={restaurantName}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100">
              <ImageIcon className="w-12 h-12 text-gray-300" />
            </div>
          )}
          
          {/* Restaurant Name Overlay */}
          <div className="absolute bottom-0 rounded-t-xl left-0 right-0 bg-black/60 p-4">
            <h3 className="text-xl font-bold text-white truncate">
              {restaurantName}
            </h3>
          </div>
        </div>

        {/* Photos Section */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-700">
              Your Photos ({photos.length})
            </h4>
            <button
              onClick={() => window.location.href = `/restaurant/${restaurantId}/${slugify(restaurantName)}`}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              View Restaurant →
            </button>
          </div>

          {/* Photo Grid */}
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {photos.map((photo, index) => (
              <div
                key={index}
                onClick={() => handlePhotoClick(index)}
                className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer group"
              >
                <img
                  src={photo.url}
                  alt={photo.caption || `Photo ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-110"
                  loading="lazy"
                />
                
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-black/0 transition-all duration-200 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <ImageIcon className="w-6 h-6 text-white" />
                  </div>
                </div>

                {/* Category Badge */}
                {photo.category && photo.category !== 'other' && (
                  <div className="absolute top-1 right-1 px-2 py-0.5 bg-white/50 rounded text-xs font-medium text-gray-700 capitalize">
                    {photo.category}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {selectedPhotoIndex !== null && (
        <PhotoLightbox
          photos={photos}
          initialIndex={selectedPhotoIndex}
          onClose={handleCloseLightbox}
        />
      )}
    </>
  );
};

export default RestaurantCard;